//Hooks
import { get } from 'svelte/store';
//Stores
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { temp_schemes } from 'src/stores/lists/scenes-derived';
import { scenes } from 'src/stores/lists/scenes';
import { dashboards } from 'src/stores/features/dashboards';
//Helpers
import { filterSensors } from 'src/components/Lists/list-helpers';
import { decimal_to_hex } from 'src/helpers/utilities';
import { real_id } from 'src/helpers/interface_helpers';
import { MODULES } from 'src/constants';

export type DeviceUsage = {
	room: string[];
	group: { _id: string; _meta: any /* group object */ }[];
	alarm: any[] /* alarm object */;
	scene: any[] /* scene objects */;
	dashboard: { _id: string; _meta: any /* dashboard object */ }[];
	thermostat: any[] /* thermostat objects */;
};

/**
 * Get array with all groups, containing the device.
 * @param deviceId - The ID of the device.
 * @returns An array of group ids and objects.
 */
export const getGroupsWithDevice = (
	deviceId: string,
): { _id: string; _meta: any /* group object */ }[] => {
	if (!deviceId) return [];

	return (
		Object.values(get(groups))
			.map((group: any) => {
				if (group.devices.includes(deviceId)) return { _id: group.id, _meta: group };
			})
			.filter((group) => group !== undefined) || []
	);
};

/**
 * Get array with all alarms, containing the device.
 * @param deviceId - The ID of the device.
 * @returns An array of alarm objects.
 */
export const getAlarmsWithDevice = (deviceId: string): any[] /* alarm object */ => {
	if (!deviceId) return [];

	const alarmSensors = [...filterSensors(Object.values(get(devices)))].map((sensor) => sensor.id);
	if (alarmSensors?.includes(deviceId)) {
		return (
			Object.values(get(scenes))
				.filter((scene: any) => {
					return scene?._gui_function === 'alarm';
				})
				.map((alarm: any) => {
					if (alarm._meta?.sensors?.includes(deviceId)) return alarm;
				})
				.filter((alarm) => alarm !== undefined) || []
		);
	} else {
		return [];
	}
};

/**
 * Get array with all thermostats, containing the device.
 * @param deviceId - The ID of the device.
 * @returns An array of thermostats objects.
 */
export const getThermostatsWithDevice = (deviceId: string): any[] /* thermostat objects */ => {
	if (!deviceId) return [];
	const device = get(devices)[deviceId];

	return (
		Object.values(get(temp_schemes))
			.map((thermostat: any) => {
				// In some very rare cases, when the thermostat object is corrupted with wrong data (or maybe there isn't data property at all), decimal id will be not present.
				// So in order to avoid errors (or runtime crashes), we will handle it with falsy check.
				// By doing it that way, we will not break the flow and will provide more flexibility with other similar cases.
				//
				// NOTE: Reference issues: https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2839
				// 		  					https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2864
				const ids = thermostat._meta?.data?.map(
					(item: any) => item?.relay_id || item?.sensor_id,
				);
				const decId = thermostat?.tschem?.r?.split(':')[1];
				const realId = decId ? decimal_to_hex(decId) : undefined;
				const devSensorNumber = thermostat?.tschem?.r?.split(':')[2]?.substr(1);

				if (MODULES.includes(device.type) || device.gen === 'GBLE') {
					decId && ids?.push(decId);
				} else {
					realId && ids?.push(realId);
				}

				if (
					// External sensors (with ids > 100) are attached to the main device
					// So we should display them for all channels of the main device
					ids?.includes(devSensorNumber > 100 ? real_id(deviceId) : deviceId)
				) {
					return thermostat;
				}
			})
			.filter((thermostat) => thermostat !== undefined) || []
	);
};

/**
 * Get array with all scenes, containing the device.
 * @param deviceId - The ID of the device.
 * @returns An array of scenes objects.
 */
export const getScenesWithDevice = (deviceId: string): any[] /* scene objects */ => {
	if (!deviceId) return [];

	return (
		Object.values(get(scenes))
			.filter((scene: any) => {
				return !scene?.tschem && scene?._gui_function !== 'alarm';
			})
			.map((scene: any) => {
				if (scene._meta?.adi?.includes(deviceId) || scene._meta?.cdi?.includes(deviceId)) {
					return scene;
				}
			})
			.filter((scene) => scene !== undefined) || []
	);
};

/**
 * Get array with all dashboards, containing the device.
 * @param deviceId - The ID of the device.
 * @returns An array of dashboards ids and objects.
 */
export const getDashboardsWithDevice = (
	deviceId: string,
): { _id: string; _meta: any /* dashboard object */ }[] => {
	if (!deviceId) return [];

	return (
		Object.values(get(dashboards))
			.map((dashboard: any) => {
				if (Object.keys(dashboard.devices).includes(deviceId))
					return {
						_id: dashboard.id,
						_meta: { ...dashboard, image: 'default' },
					};
			})
			.filter((dash) => dash !== undefined) || []
	);
};
